import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  CheckIcon,
  LockIcon,
  CloudArrowDownIcon,
  DatabaseIcon,
  InfinityIcon,
  DivideIcon,
  SpinnerIcon,
  UsersIcon,
  CalendarDotsIcon,
  BrainIcon,
  BellRingingIcon,
} from "@phosphor-icons/react";
import NumberFlow, { type Format } from "@number-flow/react";
import { cn } from "../../../lib/utils";
import { IStripePlan } from "../../../interfaces";
import { IPlanMeta } from "../interface/pricingMeta";

interface Props {
  plan: IStripePlan;
  meta: IPlanMeta;
  isAnnual: boolean;
  canStartTrial: boolean;
  onSubscribe: (planId: number) => void;
  isPending: boolean;
  pendingPlanId: number | null;
}

const AnimatedPrice: React.FC<{
  value: number;
  format: Format;
  locales?: string;
  className?: string;
}> = ({ value, format, locales = "en-US", className }) => {
  const [displayVal, setDisplayVal] = useState(0);

  useEffect(() => {
    setDisplayVal(value);
  }, [value]);

  return (
    <NumberFlow
      value={displayVal}
      format={format}
      locales={locales}
      className={className}
    />
  );
};

const PricingCard: React.FC<Props> = ({
  plan,
  meta,
  isAnnual,
  canStartTrial,
  onSubscribe,
  isPending,
  pendingPlanId,
}) => {
  const { t } = useTranslation();

  const monthly = parseFloat(plan.amount) || 0;
  const display = isAnnual ? monthly * 0.8 : monthly;
  const annualTotal = monthly * 12 * 0.8;

  const currencyCode = plan.currency?.toUpperCase() || "USD";
  const priceFormat: Format = useMemo(
    () => ({
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: currencyCode === "INR" ? 0 : 2,
      maximumFractionDigits: currencyCode === "INR" ? 0 : 2,
    }),
    [currencyCode],
  );

  const annualPriceFormat: Format = useMemo(
    () => ({
      ...priceFormat,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
    [priceFormat],
  );
  const isCurrentPlan = plan.is_current_plan;
  const isEnterprise = !!meta.enterpriseContact;
  const isThisPending = isPending && pendingPlanId === plan.id;

  /* ── CTA label ─────────────────────────────── */
  const ctaLabel = () => {
    if (isCurrentPlan) return t("pricing.your_active_plan", "Your Active Plan");
    if (isEnterprise) return t("pricing.contact_sales", "Contact Sales");
    if (plan.trial_days > 0 && canStartTrial)
      return `${t("pricing.start_trial", "Start")} ${plan.trial_days}${t("pricing.day_trial", "-Day Trial")}`;
    return t("pricing.subscribeNow", "Subscribe Now");
  };

  /* ── Fine print ─────────────────────────────── */
  const finePrint = () => {
    if (isCurrentPlan)
      return null;
    if (isEnterprise)
      return t("pricing.enterprise_reply", "Reply within 2 business hours");
    if (plan.trial_days > 0 && canStartTrial)
      return t(
        "pricing.no_charge_until",
        "No charge until Day 8 · cancel anytime",
      );
    return t("pricing.secure_payment", "Secure payment via Stripe");
  };

  /* ── Highlight row icon ─────────────────────── */
  const hlIcon = (label: string, hc: "primary" | "green" | undefined) => {
    const lc = label.toLowerCase();
    const c = hc === "green" ? "#30cd73" : "#1a3d7c";
    if (lc.includes("team") || lc.includes("seat"))
      return <UsersIcon size={14} color={c} />;
    if (lc.includes("year") || lc.includes("live"))
      return <CalendarDotsIcon size={14} color={c} />;
    if (lc.includes("ai") || lc.includes("report"))
      return <BrainIcon size={14} color={c} />;
    if (lc.includes("alert") || lc.includes("anomaly"))
      return <BellRingingIcon size={14} color={c} />;
    return <CheckIcon size={14} color={c} weight="bold" />;
  };

  /* ── Token helpers ──────────────────────────── */
  const dk = meta.dark;
  const ink = dk ? "#ffffff" : "#0f172a";
  const ink2 = dk ? "rgba(255,255,255,0.88)" : "#1e293b";
  const muted = dk ? "rgba(255,255,255,0.50)" : "#64748b";
  const sub = dk ? "rgba(255,255,255,0.09)" : "rgba(26,61,124,0.08)";

  /* ── Card container ─────────────────────────── */
  // transform lives on the wrapper so the glow border moves with the card
  const cardStyle: React.CSSProperties = {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    // slightly smaller radius when inside the 2px glow wrapper
    borderRadius: isCurrentPlan ? "19px" : "20px",
    overflow: "hidden",
    background: dk
      ? "linear-gradient(170deg, #0c1c3b 0%, #132645 55%, #162e54 100%)"
      : meta.featured
        ? "linear-gradient(176deg, #ffffff 0%, rgba(26,61,124,0.03) 100%)"
        : "#ffffff",
    border: isCurrentPlan
      ? "none"
      : dk
        ? "1.5px solid rgba(48,205,115,0.20)"
        : meta.featured
          ? "2px solid #1a3d7c"
          : "1.5px solid rgba(26,61,124,0.12)",
    boxShadow: meta.featured
      ? "0 0 0 3px rgba(26,61,124,0.12), 0 20px 60px rgba(26,61,124,0.22)"
      : dk
        ? "0 8px 32px rgba(12,28,59,0.50)"
        : "0 3px 16px rgba(26,61,124,0.08)",
  };

  return (
    <>
      <style>{`
        .pc-wrapper { transition: transform .32s cubic-bezier(.4,0,.2,1); }
        .pc-wrapper:hover { transform: translateY(-5px) !important; }
        .pc-wrapper:hover .pc-card { box-shadow: 0 16px 50px rgba(26,61,124,0.15) !important; }
        .pc-wrapper.featured { transform: translateY(-14px) scale(1.02); }
        .pc-wrapper.featured:hover { transform: translateY(-18px) scale(1.02) !important; }
        .pc-wrapper.featured:hover .pc-card { box-shadow: 0 0 0 3px rgba(26,61,124,0.16), 0 28px 70px rgba(26,61,124,0.28) !important; }
        .pc-btn { transition: all .22s cubic-bezier(.4,0,.2,1); }
        .pc-btn:not(:disabled):hover { transform: translateY(-2px); filter: brightness(1.06); }
      `}</style>
      {/* ── Glow wrapper: handles transform + animated border ── */}
      <div
        className={cn("pc-wrapper", meta.featured && "featured")}
        style={{
          position: "relative",
          borderRadius: "22px",
          ...(isCurrentPlan && {
            padding: "3px",
            overflow: "hidden",
            // outer ambient glow — not clipped by overflow:hidden
            boxShadow: dk
              ? "0 0 28px rgba(48,205,115,0.28), 0 0 56px rgba(26,61,124,0.18)"
              : "0 0 28px rgba(26,61,124,0.22), 0 0 56px rgba(48,205,115,0.14)",
          }),
        }}
      >
        {/* Spinning conic gradient — clipped to 2px border by the wrapper's padding+overflow:hidden */}
        {isCurrentPlan && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
            style={{
              position: "absolute",
              // larger than container so corners stay covered during rotation
              inset: "-100%",
              width: "300%",
              height: "300%",
              background:
                "conic-gradient(from 0deg, #1a3d7c 0%, #2a5ba8 18%, #30cd73 36%, #22c55e 50%, #30cd73 64%, #2a5ba8 82%, #1a3d7c 100%)",
              pointerEvents: "none",
            }}
          />
        )}
        <div className="pc-card" style={cardStyle}>
          {/* ── "★ Most Popular" banner ── */}
          {meta.featured && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                background: "linear-gradient(90deg, #1a3d7c, #2a5ba8)",
                color: "#fff",
                fontSize: "9px",
                fontWeight: 700,
                padding: "5px 22px",
                borderRadius: "0 0 12px 12px",
                letterSpacing: "0.10em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                zIndex: 10,
                boxShadow: "0 4px 18px rgba(26,61,124,0.35)",
              }}
            >
              {t("pricing.most_popular", "★ Most Popular")}
            </div>
          )}

          {/* ════════════════ CARD BODY ════════════════ */}
          <div
            style={{
              padding: meta.featured ? "44px 24px 0" : "28px 24px 0",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Plan name badge + scale tag row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 12px",
                  borderRadius: "999px",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  background: meta.badgeBg,
                  color: meta.badgeColor,
                  border: `1px solid ${meta.badgeBorder}`,
                }}
              >
                {plan.name}
              </span>

              {!isEnterprise && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "3px 9px",
                    borderRadius: "999px",
                    fontSize: "9px",
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    background: dk
                      ? "rgba(255,255,255,0.07)"
                      : "rgba(26,61,124,0.07)",
                    color: dk ? "rgba(255,255,255,0.46)" : "#64748b",
                    border: dk
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "1px solid rgba(26,61,124,0.18)",
                  }}
                >
                  {meta.scaleLimitLabel
                    .toLowerCase()
                    .replace(" apps", "")
                    .replace(" app", "")
                    .replace("unlimited", "∞")}
                  &nbsp;apps
                </span>
              )}
            </div>

            {/* Description */}
            <p
              style={{
                fontSize: "12.5px",
                color: muted,
                lineHeight: 1.58,
                marginBottom: "20px",
                minHeight: "38px",
              }}
            >
              {plan.description ||
                t(
                  "pricing.defaultDesc",
                  "Professional toolkit for campaign management.",
                )}
            </p>

            {/* ── Price ── */}
            <div style={{ marginBottom: "16px" }}>
              {isEnterprise ? (
                <>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "40px",
                      fontWeight: 800,
                      lineHeight: 1,
                      letterSpacing: "-0.04em",
                      color: "#fff",
                      marginBottom: "6px",
                    }}
                  >
                    Custom
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "rgba(255,255,255,0.40)",
                      marginBottom: "10px",
                    }}
                  >
                    Negotiated to your portfolio &amp; team
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "5px 12px",
                      background: "rgba(48,205,115,0.10)",
                      border: "1px solid rgba(48,205,115,0.22)",
                      borderRadius: "8px",
                    }}
                  >
                    <InfinityIcon size={12} color="#30cd73" />
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "rgba(255,255,255,0.65)",
                      }}
                    >
                      {t("pricing.unlimited_apps", "Unlimited apps")}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  {/* Main price */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "4px",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'JetBrains Mono', monospace",
                        fontSize: "40px",
                        fontWeight: 600,
                        lineHeight: 1,
                        letterSpacing: "-0.03em",
                        color: ink,
                      }}
                    >
                      <AnimatedPrice value={display} format={priceFormat} />
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        color: muted,
                        fontWeight: 500,
                        paddingBottom: "6px",
                      }}
                    >
                      /{t("common.mo", "mo")}
                    </span>
                  </div>

                  {/* Annual sub-line */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "18px",
                      marginBottom: "10px",
                    }}
                  >
                    {isAnnual ? (
                      <>
                        <span
                          style={{
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: "12px",
                            color: "#94a3b8",
                            textDecoration: "line-through",
                          }}
                        >
                          <AnimatedPrice value={monthly} format={priceFormat} />
                          /mo
                        </span>
                        <span
                          style={{
                            fontSize: "11.5px",
                            color: "#16a34a",
                            fontWeight: 600,
                          }}
                        >
                          {t("pricing.save_20", "Save 20%")} ·{" "}
                          <AnimatedPrice
                            value={annualTotal}
                            format={annualPriceFormat}
                          />
                          /yr
                        </span>
                      </>
                    ) : (
                      <span style={{ fontSize: "11.5px", color: muted }}>
                        {t(
                          "pricing.annual_switch_hint",
                          "Switch to annual — save 20%",
                        )}
                      </span>
                    )}
                  </div>

                  {/* Per-app chip */}
                  {meta.perAppBase !== null && (
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "5px 11px",
                        background: meta.featured
                          ? "linear-gradient(135deg, #dde6f5, #eef2fc)"
                          : "rgba(26,61,124,0.07)",
                        border: `1px solid ${meta.featured ? "#8aadd6" : "rgba(26,61,124,0.20)"}`,
                        borderRadius: "8px",
                      }}
                    >
                      <DivideIcon size={12} color="#1a3d7c" />
                      <span
                        style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: "12px",
                          fontWeight: 600,
                          color: meta.featured ? "#1a3d7c" : "#2a5ba8",
                        }}
                      >
                        <AnimatedPrice
                          value={isAnnual ? meta.perAppBase * 0.8 : meta.perAppBase}
                          format={priceFormat}
                        />{" "}
                        {t("pricing.per_app_mo", "/ app / mo")}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── Value bridge ── */}
            {meta.valueBridgeTitle && (
              <div
                style={{
                  background:
                    meta.tier === "growth"
                      ? "linear-gradient(118deg, rgba(48,205,115,0.07), rgba(26,61,124,0.05))"
                      : "linear-gradient(118deg, rgba(26,61,124,0.07), rgba(42,91,168,0.05))",
                  border: `1px solid ${meta.featured ? "#8aadd6" : "rgba(26,61,124,0.18)"}`,
                  borderRadius: "12px",
                  padding: "11px 14px",
                  marginBottom: "14px",
                }}
              >
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "#2a5ba8",
                    marginBottom: "3px",
                  }}
                >
                  {meta.valueBridgeTitle}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#334155",
                    lineHeight: 1.6,
                  }}
                >
                  {meta.valueBridgeBody}
                </div>
              </div>
            )}

            {/* ── Scale limit chip ── */}
            <div
              style={{
                background: meta.scaleBg,
                border: dk
                  ? "1px solid rgba(255,255,255,0.11)"
                  : `1px solid ${meta.badgeBorder}`,
                borderRadius: "13px",
                padding: "12px 15px",
                marginBottom: "12px",
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                  color: meta.scaleLabelColor,
                  marginBottom: "2px",
                }}
              >
                {t("pricing.scale_limit", "Scale limit")}
              </div>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 800,
                  color: meta.scaleValueColor,
                  letterSpacing: "-0.03em",
                }}
              >
                {meta.scaleLimitLabel}
              </div>
            </div>

            {/* ── Data parameters ── */}
            <div
              style={{
                background: dk
                  ? "rgba(255,255,255,0.05)"
                  : `linear-gradient(135deg, rgba(26,61,124,${meta.featured ? "0.07" : "0.04"}), rgba(42,91,168,${meta.featured ? "0.05" : "0.03"}))`,
                border: dk
                  ? "1px solid rgba(48,205,115,0.18)"
                  : `1px solid ${meta.featured ? "#8aadd6" : "rgba(26,61,124,0.16)"}`,
                borderRadius: "13px",
                padding: "13px 15px",
                marginBottom: "20px",
                display: "flex",
                flexDirection: "column",
                gap: "9px",
              }}
            >
              <div
                style={{
                  fontSize: "9.5px",
                  fontWeight: 700,
                  letterSpacing: "0.09em",
                  textTransform: "uppercase",
                  color: dk ? "rgba(48,205,115,0.80)" : "#2a5ba8",
                }}
              >
                {t("pricing.data_parameters", "Data Parameters")}
              </div>

              {/* Initial pull row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "11.5px",
                    fontWeight: 500,
                    color: muted,
                  }}
                >
                  <CloudArrowDownIcon
                    size={12}
                    color={dk ? "#30cd73" : "#1a3d7c"}
                  />
                  {t("pricing.initial_data_pull", "Initial Data Pull")}
                </div>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    padding: "2px 9px",
                    borderRadius: "6px",
                    background: dk
                      ? "rgba(48,205,115,0.14)"
                      : `rgba(26,61,124,${meta.featured ? "0.12" : "0.08"})`,
                    color: dk
                      ? "#30cd73"
                      : meta.featured
                        ? "#1a3d7c"
                        : "#2a5ba8",
                  }}
                >
                  {meta.dataParams.pull}
                </span>
              </div>

              {/* Live window row */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "11.5px",
                    fontWeight: 500,
                    color: muted,
                  }}
                >
                  <DatabaseIcon size={12} color={dk ? "#bfcfe8" : "#2a5ba8"} />
                  {t("pricing.live_dashboard_window", "Live Dashboard Window")}
                </div>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    padding: "2px 9px",
                    borderRadius: "6px",
                    background: dk
                      ? "rgba(191,207,232,0.14)"
                      : `rgba(42,91,168,${meta.featured ? "0.11" : "0.08"})`,
                    color: dk
                      ? "#bfcfe8"
                      : meta.featured
                        ? "#1a3d7c"
                        : "#2a5ba8",
                  }}
                >
                  {meta.dataParams.window}
                </span>
              </div>
            </div>
          </div>

          {/* ════════════════ FEATURE LIST ════════════════ */}
          <div
            style={{
              padding: "0 24px 0",
              display: "flex",
              flexDirection: "column",
              flex: 1,
            }}
          >
            <div
              style={{
                borderTop: `1px solid ${sub}`,
                paddingTop: "16px",
                marginBottom: "18px",
                flex: 1,
              }}
            >
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.13em",
                  textTransform: "uppercase",
                  color: dk ? "rgba(255,255,255,0.32)" : "#94a3b8",
                  marginBottom: "10px",
                }}
              >
                {meta.featureHeading}
              </div>

              {/* Checked / highlighted rows */}
              {meta.features.checked.map((feat, i) => {
                if (feat.highlight) {
                  const hc = feat.highlightColor;
                  const hBg =
                    hc === "green"
                      ? "rgba(48,205,115,0.07)"
                      : "rgba(26,61,124,0.07)";
                  const hBd =
                    hc === "green" ? "#86efac" : "rgba(26,61,124,0.22)";
                  const hCl = hc === "green" ? "#15803d" : "#1a3d7c";
                  return (
                    <div
                      key={i}
                      style={{
                        borderRadius: "10px",
                        padding: "8px 10px",
                        margin: "0 -10px 4px",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "9px",
                        fontSize: "12.5px",
                        lineHeight: 1.45,
                        background: hBg,
                        border: `1px solid ${hBd}`,
                      }}
                    >
                      <span
                        style={{ flexShrink: 0, marginTop: "2px", color: hCl }}
                      >
                        {hlIcon(feat.label, hc)}
                      </span>
                      <span style={{ color: ink2, fontWeight: 500 }}>
                        {feat.label}
                      </span>
                    </div>
                  );
                }
                return (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "9px",
                      padding: "6.5px 0",
                      fontSize: "12.5px",
                      lineHeight: 1.45,
                      color: ink2,
                      borderBottom: `1px solid ${sub}`,
                    }}
                  >
                    <span
                      style={{
                        flexShrink: 0,
                        marginTop: "2px",
                        color: dk ? "#30cd73" : "#22c55e",
                      }}
                    >
                      <CheckIcon size={13} weight="bold" />
                    </span>
                    <span style={{ flex: 1 }}>
                      {feat.label}
                      {feat.note && (
                        <span
                          style={{
                            fontSize: "11px",
                            color: "#94a3b8",
                            marginLeft: "4px",
                          }}
                        >
                          ({feat.note})
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}

              {/* Locked rows */}
              {meta.features.locked.map((feat, i) => (
                <div
                  key={`l${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    padding: "6.5px 0",
                    fontSize: "12.5px",
                    lineHeight: 1.45,
                    color: "#94a3b8",
                    borderBottom: `1px solid ${sub}`,
                  }}
                >
                  <span style={{ flexShrink: 0, color: "#cbd5e1" }}>
                    <LockIcon size={12} />
                  </span>
                  <span style={{ flex: 1 }}>{feat.label}</span>
                  <span
                    style={{
                      flexShrink: 0,
                      padding: "2px 7px",
                      borderRadius: "999px",
                      fontSize: "9px",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      background: "rgba(26,61,124,0.07)",
                      color: "#64748b",
                      border: "1px solid rgba(26,61,124,0.16)",
                    }}
                  >
                    {feat.requiresPlan}
                  </span>
                </div>
              ))}
            </div>

            {/* ── CTA button ── */}
            <button
              disabled={isCurrentPlan || (isPending && !isThisPending)}
              onClick={() =>
                !isCurrentPlan && !isEnterprise && onSubscribe(plan.id)
              }
              className="pc-btn"
              style={{
                display: "block",
                width: "100%",
                padding: "13px 0",
                marginBottom: "8px",
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: "14px",
                letterSpacing: "0.01em",
                textAlign: "center",
                cursor: isCurrentPlan ? "default" : "pointer",
                border: "none",
                ...ctaButtonStyle(meta.ctaStyle, isCurrentPlan),
              }}
            >
              {isThisPending ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <SpinnerIcon size={15} className="animate-spin" />
                  Loading…
                </span>
              ) : (
                ctaLabel()
              )}
            </button>

            {/* Fine print */}
            <p
              style={{
                textAlign: "center",
                fontSize: "11px",
                color: dk ? "rgba(255,255,255,0.28)" : "#94a3b8",
                marginBottom: "24px",
              }}
            >
              {finePrint()}
            </p>
          </div>
        </div>{" "}
        {/* end pc-card */}
      </div>{" "}
      {/* end pc-wrapper */}
    </>
  );
};

/* ── CTA button style helper ── */
function ctaButtonStyle(
  style: IPlanMeta["ctaStyle"],
  isCurrentPlan: boolean,
): React.CSSProperties {
  if (isCurrentPlan)
    return {
      border: "1px solid #1a3d7c",
      color: "#1a3d7c",
      boxShadow: "0 6px 22px rgba(34,197,94,0.38)",
      opacity: 0.5
    };
  switch (style) {
    case "ghost":
      return {
        background: "transparent",
        color: "#1e293b",
        border: "1.5px solid rgba(26,61,124,0.28)",
      };
    case "green":
      return {
        background: "linear-gradient(135deg, #22c55e, #30cd73)",
        color: "#fff",
        boxShadow: "0 6px 22px rgba(48,205,115,0.32)",
      };
    case "dark":
      return {
        background:
          "linear-gradient(135deg, rgba(48,205,115,0.18), rgba(26,61,124,0.15))",
        color: "#fff",
        border: "1.5px solid rgba(48,205,115,0.35)",
      };
    case "primary":
    default:
      return {
        background: "linear-gradient(135deg, #1a3d7c, #2a5ba8)",
        color: "#fff",
        boxShadow: "0 6px 24px rgba(26,61,124,0.30)",
      };
  }
}

export default PricingCard;
